import { Link } from "react-router-dom";
import Avatar from "react-avatar";
import { UncontrolledTooltip } from "reactstrap";
import { FlagImage } from "react-international-phone";
import { useCountries } from "hooks/useCountries";
import { CampaignShort } from "types/campaign";
import { Milestone } from "types/collaboration";
import { formatAmount, formatNumber } from "utils/helpers";

const STATS_STATUSES = ["approved", "completed"];

const getMilestoneLabel = (milestone: Milestone) =>
    `#${milestone.sort_order} ${milestone.gig_type?.name ?? "Milestone"}`;

interface CampaignListCardProps {
    campaign: CampaignShort;
    status: string;
}

const CampaignListCard = ({ campaign, status }: CampaignListCardProps) => {
    const { countries } = useCountries();
    const milestones = [...(campaign.milestones ?? [])].sort((a, b) => a.sort_order - b.sort_order);
    const isVisible = campaign.is_public === "y";
    const showStats = STATS_STATUSES.includes(status);
    const countryCode = campaign.country;
    const countryOption = countries.find(
        (item) => item.value === (countryCode || "").toUpperCase()
    );
    const countryFlagId = `campaign-list-flag-${campaign.uuid}`;

    return (
        <div className="campaign-card p-0">
            <div className="d-flex justify-content-between align-items-center h-100 flex-fill">
                <div className="p-3 h-100 d-flex align-items-center">
                    <div className="d-flex gap-3">
                        <Link to={`/campaign/${campaign.uuid}/applications`}>
                            {campaign.brand?.img_url ? (
                                <div
                                    className="campaign-hero-img bg-image bgc-white"
                                    style={{ backgroundImage: `url(${campaign.brand.img_url})` }}
                                />
                            ) : (
                                <Avatar size="118px" round="6px" name={campaign.name} maxInitials={1} />
                            )}
                        </Link>
                        <div className="d-flex flex-column justify-content-between flex-wrap gap-3">
                            <div className="d-flex flex-column justify-content-start gap-2">
                                <h6 className="campaign-name m-0">
                                    <Link to={`/campaign/${campaign.uuid}/applications`} className="text-dark text-decoration-none">
                                        {campaign.platform === "ig" && (
                                            <i className="ri-instagram-line linked-posts-yt-logo mt-1 me-2" style={{ color: 'red' }} />
                                        )}
                                        {campaign.platform === "yt" && (
                                            <i className="ri-youtube-fill linked-posts-yt-logo mt-1 me-2" style={{ color: 'red' }} />
                                        )}
                                        {campaign.name}
                                    </Link>
                                </h6>
                                <span className="published-at">{campaign?.active_at ?? campaign.created}</span>
                                {
                                    (campaign.mode === "offline" || campaign.status === 'completed') && (
                                        <div className="d-flex align-items-center gap-2">
                                            {campaign.mode === "offline" && (
                                                <span className="badge language-badge">Track Only</span>
                                            )}
                                            {campaign.status === "completed" && (
                                                <span className="badge language-badge">Completed</span>
                                            )}
                                        </div>
                                    )
                                }
                                <div className="campaign-card-meta campaign-card-meta--inline row g-0 mx-0 mt-0">
                                    <div className="col-auto campaign-card-meta-item">
                                        <span className="campaign-card-meta-label">Agency</span>
                                        <span className="campaign-card-meta-value d-flex align-items-center gap-2">
                                            {
                                                countryCode &&
                                                <>
                                                    <span id={countryFlagId} className="d-inline-flex">
                                                        <FlagImage
                                                            iso2={countryOption?.iso2 || countryCode.toLowerCase()}
                                                            size="14px"
                                                        />
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement="top"
                                                        target={countryFlagId}
                                                        fade={false}
                                                    >
                                                        {countryOption?.label || countryCode.toUpperCase()}
                                                    </UncontrolledTooltip>
                                                </>
                                            }
                                            <span title={campaign.agency?.name}>
                                                {campaign.agency?.name ?? "—"}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="col-auto campaign-card-meta-item">
                                        <span className="campaign-card-meta-label">Brand</span>
                                        <span className="campaign-card-meta-value" title={campaign.brand?.name}>
                                            {campaign.brand?.name ?? "—"}
                                        </span>
                                    </div>
                                    <div className="col-auto campaign-card-meta-item campaign-card-meta-item--milestones">
                                        <span className="campaign-card-meta-label">Milestones</span>
                                        <div className="campaign-card-meta-milestones">
                                            {milestones.length > 0 ? (
                                                milestones.map((milestone) => (
                                                    <span key={milestone.id} className="campaign-milestone-chip">
                                                        {getMilestoneLabel(milestone)}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="campaign-card-meta-value">—</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-auto campaign-card-meta-item">
                                        <span className="campaign-card-meta-label">Visible</span>
                                        <span className="campaign-card-meta-value">{isVisible ? "Yes" : "No"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {showStats && (
                    <div className="h-100 w-510px d-none d-md-block">
                        {campaign.mode === "offline" ? (
                            <div className="d-flex align-items-center justify-content-end" style={{ height: "166px" }}>
                                <div className="campaign-stat-item h-100" style={{ width: "140px", borderLeft: "1px solid #EDEBEC" }}>
                                    <span className="stat-value">{formatNumber(campaign.views_count)}</span>
                                    <span className="stat-label">Views</span>
                                </div>
                                <div className="campaign-stat-item h-100" style={{ width: "139px", borderLeft: "1px solid #EDEBEC" }}>
                                    <span className="stat-value">{formatNumber(campaign.interactions_count)}</span>
                                    <span className="stat-label">Interactions</span>
                                </div>
                                <div className="campaign-stat-item h-100" style={{ width: "129px", borderLeft: "1px solid #EDEBEC" }}>
                                    <span className="stat-value">{formatNumber(campaign.collaboration_count)}</span>
                                    <span className="stat-label">Collaborations</span>
                                </div>
                            </div>
                        ) : (
                            <div className="row campaign-stats h-100 justify-content-end">
                                {Number(campaign.version) >= 2 ? (
                                    <div className="col-4 campaign-stat-item">
                                        <span className="stat-value">{campaign.requirements?.creator_count?.max}</span>
                                        <span className="stat-label">No. of Creators</span>
                                    </div>
                                ) : (
                                    <div className="col-4 campaign-stat-item">
                                        <span className={`stat-value ${Number(campaign.remaining_budget ?? 0) < 0 ? "text-danger" : "text-success"}`}>
                                            {campaign.remaining_budget ? formatAmount(campaign.remaining_budget, campaign.currency?.code, campaign.currency?.symbol, campaign?.currency?.locale) : "-"}
                                        </span>
                                        <span className="stat-label">Budget Remaining</span>
                                    </div>
                                )}
                                <div className="col-4 campaign-stat-item">
                                    <span className="stat-value">{formatNumber(campaign.views_count)}</span>
                                    <span className="stat-label">Views</span>
                                </div>
                                <div className="col-4 campaign-stat-item">
                                    <span className="stat-value">{formatNumber(campaign.interactions_count)}</span>
                                    <span className="stat-label">Interactions</span>
                                </div>
                                <div className="col-4 campaign-stat-item">
                                    <span className="stat-value">{formatAmount(campaign.budget, campaign.currency?.code, campaign.currency?.symbol, campaign?.currency?.locale)}</span>
                                    <span className="stat-label">Creator Budget</span>
                                </div>
                                <div className="col-4 campaign-stat-item">
                                    <span className="stat-value">{formatNumber(campaign.total_application_count)}</span>
                                    <span className="stat-label">Applications</span>
                                </div>
                                <div className="col-4 campaign-stat-item">
                                    <span className="stat-value">{formatNumber(campaign.collaboration_count)}</span>
                                    <span className="stat-label">Collaborations</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CampaignListCard;
